// NearMiss.js
// This is the model for the Near Miss form

import { validateNearMiss } from "../utils/nearMissValidation";
import { FormPhotos } from "../../FormPhotos/FormPhotos";

export class NearMiss {
  constructor(data = {}) {
    this.id = data.id || null;
    this.incident_date = data.incident_date || null;
    this.incident_time = data.incident_time || null;
    this.location = data.location || null;
    this.site = data.site || null;
    this.reported_to = data.reported_to || null;
    this.reported_to_name = data.reported_to_name || null;
    this.reported_by = data.reported_by || null;
    this.reported_by_name = data.reported_by_name || null;
    this.job_title = data.job_title || null;
    this.near_miss_description = data.near_miss_description || null;
    this.pot_haz_stf = data.pot_haz_stf || false;
    this.pot_haz_fo = data.pot_haz_fo || false;
    this.pot_haz_ce = data.pot_haz_ce || false;
    this.pot_haz_eh = data.pot_haz_eh || false;
    this.pot_haz_ef = data.pot_haz_ef || false;
    this.pot_haz_ippe = data.pot_haz_ippe || false;
    this.pot_haz_mhi = data.pot_haz_mhi || false;
    this.pot_haz_feh = data.pot_haz_feh || false;
    this.pot_haz_csi = data.pot_haz_csi || false;
    this.pot_haz_other = data.pot_haz_other || null;
    this.contributing_factors = data.contributing_factors || null;
    this.actions_taken = data.actions_taken || null;
    this.sag_corrective_actions = data.sag_corrective_actions || null;
    this.immediate_ass_yes = data.immediate_ass_yes || false;
    this.immediate_ass_no = data.immediate_ass_no || false;
    this.immediate_ass_yes_description =
      data.immediate_ass_yes_description || null;
    this.signature = data.signature
      ? "data:image/png;base64," + data.signature
      : "";
    this.signature_employee_id = data.signature_employee_id || null;
    this.signature_employee_name = data.signature_employee_name || null;
    this.signature_date = data.signature_date || this._formatDate(new Date());
    this.created_at = data.created_at || null;
    this.updated_at = data.updated_at || null;
    this.created_by = data.created_by || null;
    this.created_by_name = data.created_by_name
      ? data.created_by_name + " - " + data.created_by
      : null;
    this.updated_by = data.updated_by || null;
    this.updated_by_name = data.updated_by_name
      ? data.updated_by_name + " - " + data.updated_by
      : null;
    this.status = data.status || null;

    // Photos array - populated with presigned URLs from backend
    this.photos = data.photos
      ? data.photos.map((photo) => new FormPhotos(photo))
      : [];

    // error data
    this.errors = null;
  }

  _formatDate(date) {
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    const year = date.getFullYear();
    return `${year}-${month}-${day}`;
  }

  updateNearMissId(nearMissId) {
    this.id = nearMissId;
  }

  updateIncidentDate(incidentDate) {
    if (!incidentDate) {
      this.incident_date = null;
      return;
    }
    this.incident_date = this._formatDate(incidentDate);
  }

  updateIncidentTime(incidentTime) {
    this.incident_time = incidentTime;
  }

  updateLocation(location) {
    this.location = location;
  }

  updateSite(site) {
    this.site = site;
  }

  updateReportedTo(reportedTo) {
    if (!reportedTo) {
      this.reported_to = null;
      return;
    }
    this.reported_to = reportedTo;
  }

  updateReportedToName(reportedToName) {
    if (!reportedToName) {
      this.reported_to_name = null;
      return;
    }
    this.reported_to_name = reportedToName;
  }

  updateReportedBy(reportedBy) {
    if (!reportedBy) {
      this.reported_by = null;
      return;
    }
    this.reported_by = reportedBy;
  }

  updateReportedByName(reportedByName) {
    if (!reportedByName) {
      this.reported_by_name = null;
      return;
    }
    this.reported_by_name = reportedByName;
  }

  updateJobTitle(jobTitle) {
    if (!jobTitle) {
      this.job_title = null;
      return;
    }
    this.job_title = jobTitle;
  }

  updateNearMissDescription(nearMissDescription) {
    this.near_miss_description = nearMissDescription;
  }

  updatePotHazSTF(potHazSTF) {
    this.pot_haz_stf = potHazSTF;
  }

  updatePotHazFO(potHazFO) {
    this.pot_haz_fo = potHazFO;
  }

  updatePotHazCE(potHazCE) {
    this.pot_haz_ce = potHazCE;
  }

  updatePotHazEH(potHazEH) {
    this.pot_haz_eh = potHazEH;
  }

  updatePotHazEF(potHazEF) {
    this.pot_haz_ef = potHazEF;
  }

  updatePotHazIPPE(potHazIPPE) {
    this.pot_haz_ippe = potHazIPPE;
  }

  updatePotHazMHI(potHazMHI) {
    this.pot_haz_mhi = potHazMHI;
  }

  updatePotHazFEH(potHazFEH) {
    this.pot_haz_feh = potHazFEH;
  }

  updatePotHazCSI(potHazCSI) {
    this.pot_haz_csi = potHazCSI;
  }

  updatePotHazOther(potHazOther) {
    this.pot_haz_other = potHazOther;
  }

  updateContributingFactors(contributingFactors) {
    this.contributing_factors = contributingFactors;
  }

  updateActionsTaken(actionsTaken) {
    this.actions_taken = actionsTaken;
  }

  updateSagCorrectiveActions(sagCorrectiveActions) {
    this.sag_corrective_actions = sagCorrectiveActions;
  }

  updateImmediateAssYes(immediateAssYes) {
    if (!immediateAssYes) {
      this.immediate_ass_yes = false;
      return;
    }
    this.immediate_ass_yes = immediateAssYes;
    this.immediate_ass_no = false;
  }

  updateImmediateAssNo(immediateAssNo) {
    if (!immediateAssNo) {
      this.immediate_ass_no = false;
      return;
    }
    this.immediate_ass_no = immediateAssNo;
    this.immediate_ass_yes = false;
    this.immediate_ass_yes_description = null;
  }

  updateImmediateAssYesDescription(immediateAssYesDescription) {
    this.immediate_ass_yes_description = immediateAssYesDescription;
  }

  updateSignature(signature) {
    if (!signature) {
      this.signature = null;
      return;
    }
    this.signature = signature;
  }

  updateSignatureDate(signatureDate) {
    if (!signatureDate) {
      this.signature_date = null;
      return;
    }
    this.signature_date = this._formatDate(signatureDate);
  }

  updateSignatureEmployeeId(signatureEmployeeId) {
    if (!signatureEmployeeId) {
      this.signature_employee_id = null;
      return;
    }
    this.signature_employee_id = signatureEmployeeId;
  }

  updateSignatureEmployeeName(signatureEmployeeName) {
    if (!signatureEmployeeName) {
      this.signature_employee_name = null;
      return;
    }
    this.signature_employee_name = signatureEmployeeName;
  }

  // Helper for single and multi-field patches to be sent via websocket
  toPatch(fields) {
    const fieldMap = {
      incidentDate: "incident_date",
      incidentTime: "incident_time",
      location: "location",
      site: "site",
      reportedTo: "reported_to",
      reportedBy: "reported_by",
      jobTitle: "job_title",
      nearMissDescription: "near_miss_description",
      potHazStf: "pot_haz_stf",
      potHazFo: "pot_haz_fo",
      potHazCe: "pot_haz_ce",
      potHazEh: "pot_haz_eh",
      potHazEf: "pot_haz_ef",
      potHazIppe: "pot_haz_ippe",
      potHazMhi: "pot_haz_mhi",
      potHazFeh: "pot_haz_feh",
      potHazCsi: "pot_haz_csi",
      potHazOther: "pot_haz_other",
      contributingFactors: "contributing_factors",
      actionsTaken: "actions_taken",
      sagCorrectiveActions: "sag_corrective_actions",
      immediateAssYes: "immediate_ass_yes",
      immediateAssNo: "immediate_ass_no",
      immediateAssYesDescription: "immediate_ass_yes_description",
      signature: "signature",
      signatureDate: "signature_date",
      signatureEmployeeId: "signature_employee_id",
      status: "status",
    };

    const patch = { patch_type: "near_miss_main" };
    for (const [key, value] of Object.entries(fields)) {
      const dbField = fieldMap[key];
      if (dbField) {
        patch[dbField] = value;
      }
    }
    return patch;
  }

  // Helper function to check if the del data has validation errors,the power of recursion...
  hasValidationErrors(errors) {
    const checkErrors = (err) => {
      if (err == null) return false;
      if (typeof err === "string") {
        return err.trim() !== "";
      }
      if (Array.isArray(err)) {
        // Recursively check each item in the array
        return err.some((item) => checkErrors(item));
      }
      if (typeof err === "object") {
        // Recursively check all object keys
        return Object.keys(err).some((key) => checkErrors(err[key]));
      }
      return false;
    };

    return checkErrors(errors);
  }

  saveDraftNearMiss(userContext) {
    const signature = this.signature
      ? this.signature.replace("data:image/png;base64,", "").trim()
      : null;

    const nearMissData = {
      id: this.id,
      incident_date: this.incident_date,
      incident_time: this.incident_time,
      location: this.location,
      site: this.site,
      reported_to: this.reported_to,
      reported_by: this.reported_by,
      job_title: this.job_title,
      near_miss_description: this.near_miss_description,
      pot_haz_stf: this.pot_haz_stf,
      pot_haz_fo: this.pot_haz_fo,
      pot_haz_ce: this.pot_haz_ce,
      pot_haz_eh: this.pot_haz_eh,
      pot_haz_ef: this.pot_haz_ef,
      pot_haz_ippe: this.pot_haz_ippe,
      pot_haz_mhi: this.pot_haz_mhi,
      pot_haz_feh: this.pot_haz_feh,
      pot_haz_csi: this.pot_haz_csi,
      pot_haz_other: this.pot_haz_other,
      contributing_factors: this.contributing_factors,
      actions_taken: this.actions_taken,
      sag_corrective_actions: this.sag_corrective_actions,
      immediate_ass_yes: this.immediate_ass_yes,
      immediate_ass_no: this.immediate_ass_no,
      immediate_ass_yes_description: this.immediate_ass_yes_description,
      signature: signature,
      signature_employee_id: this.signature_employee_id,
      signature_date: this.signature_date,
      updated_by: userContext.employee_id,
    };
    return nearMissData;
  }

  saveDraftNearMissData(nearMissMode, userContext) {
    // For draft saves, we don't validate - just package the data
    const signature = this.signature
      ? this.signature.replace("data:image/png;base64,", "").trim()
      : null;

    const nearMissData = {
      id: this.id,
      incident_date: this.incident_date,
      incident_time: this.incident_time,
      location: this.location,
      site: this.site,
      reported_to: this.reported_to,
      reported_by: this.reported_by,
      job_title: this.job_title,
      near_miss_description: this.near_miss_description,
      pot_haz_stf: this.pot_haz_stf,
      pot_haz_fo: this.pot_haz_fo,
      pot_haz_ce: this.pot_haz_ce,
      pot_haz_eh: this.pot_haz_eh,
      pot_haz_ef: this.pot_haz_ef,
      pot_haz_ippe: this.pot_haz_ippe,
      pot_haz_mhi: this.pot_haz_mhi,
      pot_haz_feh: this.pot_haz_feh,
      pot_haz_csi: this.pot_haz_csi,
      pot_haz_other: this.pot_haz_other,
      contributing_factors: this.contributing_factors,
      actions_taken: this.actions_taken,
      sag_corrective_actions: this.sag_corrective_actions,
      immediate_ass_yes: this.immediate_ass_yes,
      immediate_ass_no: this.immediate_ass_no,
      immediate_ass_yes_description: this.immediate_ass_yes_description,
      signature: signature,
      signature_employee_id: this.signature_employee_id,
      signature_date: this.signature_date,
      updated_by: userContext.employee_id,
    };
    return nearMissData;
  }

  submitNearMiss(userContext) {
    // Validating the near miss data to be submitted
    const nearMissErrors = validateNearMiss(this);
    if (this.hasValidationErrors(nearMissErrors)) {
      this.errors = nearMissErrors;
      return { errors: nearMissErrors };
    } else {
      this.errors = null;
    }
    // Getting user name and id from the provided user context
    // Turning the near miss data into a json object
    // Removing the data:image/png;base64, from the signature
    const signature = this.signature
      ? this.signature.replace("data:image/png;base64,", "").trim()
      : null;

    const nearMissData = {
      id: this.id,
      incident_date: this.incident_date,
      incident_time: this.incident_time,
      location: this.location,
      site: this.site,
      reported_to: this.reported_to,
      reported_by: this.reported_by,
      job_title: this.job_title,
      near_miss_description: this.near_miss_description,
      pot_haz_stf: this.pot_haz_stf,
      pot_haz_fo: this.pot_haz_fo,
      pot_haz_ce: this.pot_haz_ce,
      pot_haz_eh: this.pot_haz_eh,
      pot_haz_ef: this.pot_haz_ef,
      pot_haz_ippe: this.pot_haz_ippe,
      pot_haz_mhi: this.pot_haz_mhi,
      pot_haz_feh: this.pot_haz_feh,
      pot_haz_csi: this.pot_haz_csi,
      pot_haz_other: this.pot_haz_other,
      contributing_factors: this.contributing_factors,
      actions_taken: this.actions_taken,
      sag_corrective_actions: this.sag_corrective_actions,
      immediate_ass_yes: this.immediate_ass_yes,
      immediate_ass_no: this.immediate_ass_no,
      immediate_ass_yes_description: this.immediate_ass_yes_description,
      signature: signature,
      signature_employee_id: this.signature_employee_id,
      signature_date: this.signature_date,
      updated_by: userContext?.employee_id || null,
    };
    return nearMissData;
  }

  submitNearMissData(userContext) {
    // Validating the near miss data to be submitted
    const { fieldErrors, notificationErrors } = validateNearMiss(this);
    if (this.hasValidationErrors(fieldErrors)) {
      this.errors = fieldErrors; // Set camelCase errors for form display
      console.log("Near miss errors:", fieldErrors);
      return { errors: notificationErrors }; // Return nice errors for notification
    } else {
      this.errors = null;
    }

    // Same packaging as saveDraftNearMissData
    return this.saveDraftNearMissData(null, userContext);
  }

  /**
   * Validate form for submission and return minimal payload.
   * Data is already saved via WebSocket autosave, so we only need to send the ID.
   */
  submitNearMissSimple(userContext) {
    // Frontend validation for UX
    const { fieldErrors, notificationErrors } = validateNearMiss(this);
    if (this.hasValidationErrors(fieldErrors)) {
      this.errors = fieldErrors; // Set camelCase errors for form display
      return { errors: notificationErrors }; // Return nice errors for notification
    }
    this.errors = null;
    // Return minimal payload - data already persisted via autosave
    return { near_miss_id: this.id };
  }
}
